import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { useSettings } from "../../contexts/SettingsContext";
import { formatErr } from "../../lib/api";
import MediaUploader from "../MediaUploader";

// The About page schema (kept in sync with backend DEFAULT_SETTINGS.about_page).
// Every subsection is edited via one draft object; on Save we PUT { about_page: draft }.
const EMPTY_VALUE = { title: "", description: "" };
const EMPTY_LOGO = { name: "", image_url: "", link: "" };

const FALLBACK = {
  hero: { eyebrow: "", heading: "", body: "", image_url: "", video_url: "", video_webm: "", video_poster: "" },
  story: { enabled: false, eyebrow: "", heading: "", body: "", image_url: "" },
  founder: { enabled: false, name: "", title: "", quote: "", photo_url: "" },
  values: [],
  press_logos: [],
  closing: { eyebrow: "", heading: "", cta_label: "", cta_link: "/shop" },
};

export default function AboutPageAdmin() {
  const { settings, save, loaded } = useSettings();
  const [draft, setDraft] = useState(FALLBACK);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loaded) return;
    setDraft({ ...FALLBACK, ...(settings.about_page || {}) });
  }, [loaded, settings.about_page]);

  const setSection = (section, patch) =>
    setDraft(d => ({ ...d, [section]: { ...d[section], ...patch } }));

  // ---- values (dynamic cards) ----
  const addValue = () => setDraft(d => ({ ...d, values: [...(d.values || []), { ...EMPTY_VALUE }] }));
  const removeValue = (i) => setDraft(d => ({ ...d, values: d.values.filter((_, idx) => idx !== i) }));
  const patchValue = (i, patch) =>
    setDraft(d => ({ ...d, values: d.values.map((v, idx) => (idx === i ? { ...v, ...patch } : v)) }));
  const moveValue = (i, dir) =>
    setDraft(d => {
      const next = [...d.values];
      const j = i + dir;
      if (j < 0 || j >= next.length) return d;
      [next[i], next[j]] = [next[j], next[i]];
      return { ...d, values: next };
    });

  // ---- press logos ----
  const addLogo = () => setDraft(d => ({ ...d, press_logos: [...(d.press_logos || []), { ...EMPTY_LOGO }] }));
  const removeLogo = (i) => setDraft(d => ({ ...d, press_logos: d.press_logos.filter((_, idx) => idx !== i) }));
  const patchLogo = (i, patch) =>
    setDraft(d => ({ ...d, press_logos: d.press_logos.map((l, idx) => (idx === i ? { ...l, ...patch } : l)) }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await save({ about_page: draft });
      toast.success("About page updated");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => setDraft({ ...FALLBACK, ...(settings.about_page || {}) });

  if (!loaded) return <p className="text-muted text-xs uppercase tracking-[0.2em]">Loading…</p>;

  return (
    <form onSubmit={submit} className="space-y-8 max-w-3xl" data-testid="about-page-form">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-muted text-sm">Edit every section of the public /about page. Changes go live immediately.</p>
        <Link to="/about" target="_blank" className="text-xs uppercase tracking-[0.2em] text-gold hover:underline inline-flex items-center gap-1.5" data-testid="about-preview-link">
          <ExternalLink size={12} /> Preview
        </Link>
      </div>

      {/* HERO */}
      <fieldset className="bg-[#141414] p-8 space-y-5 border border-[rgba(244,244,240,0.04)]">
        <legend className="text-xs uppercase tracking-[0.25em] text-gold px-3">Hero</legend>
        <Text testid="about-hero-eyebrow" label="Eyebrow (small caps)" value={draft.hero.eyebrow} onChange={(v) => setSection("hero", { eyebrow: v })} />
        <Text testid="about-hero-heading" label="Heading" value={draft.hero.heading} onChange={(v) => setSection("hero", { heading: v })} />
        <Textarea testid="about-hero-body" label="Body Paragraph" value={draft.hero.body} onChange={(v) => setSection("hero", { body: v })} />

        <div className="space-y-2">
          <label className="text-xs uppercase tracking-[0.2em] text-muted block">Hero Image</label>
          <MediaUploader
            images={draft.hero.image_url ? [draft.hero.image_url] : []}
            videos={[]}
            accept="image"
            onChange={({ images }) => setSection("hero", { image_url: images[0] || "" })}
          />
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted/70">Used as poster if a video is uploaded, or as the standalone hero visual.</p>
        </div>

        <div className="space-y-2">
          <label className="text-xs uppercase tracking-[0.2em] text-muted block">Hero Video (optional — overrides the image when set)</label>
          <MediaUploader
            images={[]}
            videos={draft.hero.video_url ? [draft.hero.video_url] : []}
            accept="video"
            onChange={({ videos }) => {
              const url = videos[0] || "";
              if (!url) setSection("hero", { video_url: "", video_webm: "", video_poster: "" });
              else setSection("hero", { video_url: url });
            }}
            onVideoOptimized={({ webm_url, poster_url }) =>
              setSection("hero", { video_webm: webm_url || "", video_poster: poster_url || "" })
            }
          />
          {draft.hero.video_url && (draft.hero.video_webm || draft.hero.video_poster) && (
            <p className="text-[10px] uppercase tracking-[0.18em] text-gold">
              ✓ Optimised — WebM {draft.hero.video_webm ? "ready" : "—"} · Poster {draft.hero.video_poster ? "ready" : "—"}
            </p>
          )}
        </div>
      </fieldset>

      {/* OUR STORY */}
      <fieldset className="bg-[#141414] p-8 space-y-5 border border-[rgba(244,244,240,0.04)]">
        <div className="flex items-center justify-between">
          <legend className="text-xs uppercase tracking-[0.25em] text-gold px-3">Our Story (optional section)</legend>
          <Toggle checked={!!draft.story.enabled} onChange={(v) => setSection("story", { enabled: v })} testid="about-story-toggle" />
        </div>
        {draft.story.enabled && (
          <>
            <Text testid="about-story-eyebrow" label="Eyebrow" value={draft.story.eyebrow} onChange={(v) => setSection("story", { eyebrow: v })} />
            <Text testid="about-story-heading" label="Heading" value={draft.story.heading} onChange={(v) => setSection("story", { heading: v })} />
            <Textarea testid="about-story-body" label="Body" value={draft.story.body} onChange={(v) => setSection("story", { body: v })} />
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-[0.2em] text-muted block">Story Image (optional)</label>
              <MediaUploader
                images={draft.story.image_url ? [draft.story.image_url] : []}
                videos={[]}
                accept="image"
                onChange={({ images }) => setSection("story", { image_url: images[0] || "" })}
              />
            </div>
          </>
        )}
      </fieldset>

      {/* FOUNDER */}
      <fieldset className="bg-[#141414] p-8 space-y-5 border border-[rgba(244,244,240,0.04)]">
        <div className="flex items-center justify-between">
          <legend className="text-xs uppercase tracking-[0.25em] text-gold px-3">Founder Quote (optional section)</legend>
          <Toggle checked={!!draft.founder.enabled} onChange={(v) => setSection("founder", { enabled: v })} testid="about-founder-toggle" />
        </div>
        {draft.founder.enabled && (
          <>
            <Text testid="about-founder-name" label="Name" value={draft.founder.name} onChange={(v) => setSection("founder", { name: v })} />
            <Text testid="about-founder-title" label="Title" value={draft.founder.title} onChange={(v) => setSection("founder", { title: v })} />
            <Textarea testid="about-founder-quote" label="Quote / Message" value={draft.founder.quote} onChange={(v) => setSection("founder", { quote: v })} />
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-[0.2em] text-muted block">Founder Photo</label>
              <MediaUploader
                images={draft.founder.photo_url ? [draft.founder.photo_url] : []}
                videos={[]}
                accept="image"
                onChange={({ images }) => setSection("founder", { photo_url: images[0] || "" })}
              />
            </div>
          </>
        )}
      </fieldset>

      {/* VALUE CARDS */}
      <fieldset className="bg-[#141414] p-8 space-y-5 border border-[rgba(244,244,240,0.04)]">
        <div className="flex items-center justify-between">
          <legend className="text-xs uppercase tracking-[0.25em] text-gold px-3">Value Cards ({(draft.values || []).length})</legend>
          <button type="button" onClick={addValue} className="text-xs uppercase tracking-[0.2em] text-gold inline-flex items-center gap-1.5 hover:opacity-80" data-testid="about-add-value">
            <Plus size={12} /> Add card
          </button>
        </div>
        {(draft.values || []).length === 0 && <p className="text-xs text-muted">No cards yet. Add one to feature a brand value on the About page.</p>}
        <div className="space-y-4">
          {(draft.values || []).map((v, i) => (
            <div key={i} className="border border-[rgba(244,244,240,0.06)] p-5 space-y-3 relative" data-testid={`about-value-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.22em] text-muted">Card #{i + 1}</span>
                <div className="flex gap-2">
                  <button type="button" onClick={() => moveValue(i, -1)} disabled={i === 0} className="text-muted hover:text-gold disabled:opacity-30" title="Move up" data-testid={`about-value-up-${i}`}>
                    <ArrowUp size={14} />
                  </button>
                  <button type="button" onClick={() => moveValue(i, 1)} disabled={i === draft.values.length - 1} className="text-muted hover:text-gold disabled:opacity-30" title="Move down" data-testid={`about-value-down-${i}`}>
                    <ArrowDown size={14} />
                  </button>
                  <button type="button" onClick={() => removeValue(i)} className="text-red-400/70 hover:text-red-400" title="Delete" data-testid={`about-value-remove-${i}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <Text testid={`about-value-title-${i}`} label="Title" value={v.title} onChange={(x) => patchValue(i, { title: x })} />
              <Textarea testid={`about-value-desc-${i}`} rows={3} label="Description" value={v.description} onChange={(x) => patchValue(i, { description: x })} />
            </div>
          ))}
        </div>
      </fieldset>

      {/* PRESS LOGOS */}
      <fieldset className="bg-[#141414] p-8 space-y-5 border border-[rgba(244,244,240,0.04)]">
        <div className="flex items-center justify-between">
          <legend className="text-xs uppercase tracking-[0.25em] text-gold px-3">Press / As Seen In ({(draft.press_logos || []).length})</legend>
          <button type="button" onClick={addLogo} className="text-xs uppercase tracking-[0.2em] text-gold inline-flex items-center gap-1.5 hover:opacity-80" data-testid="about-add-logo">
            <Plus size={12} /> Add logo
          </button>
        </div>
        {(draft.press_logos || []).length === 0 && <p className="text-xs text-muted">Add press mentions or magazine logos to appear on the About page.</p>}
        <div className="space-y-4">
          {(draft.press_logos || []).map((logo, i) => (
            <div key={i} className="border border-[rgba(244,244,240,0.06)] p-5 space-y-3" data-testid={`about-logo-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.22em] text-muted">Logo #{i + 1}</span>
                <button type="button" onClick={() => removeLogo(i)} className="text-red-400/70 hover:text-red-400" data-testid={`about-logo-remove-${i}`}>
                  <Trash2 size={14} />
                </button>
              </div>
              <Text testid={`about-logo-name-${i}`} label="Publication name (alt text)" value={logo.name} onChange={(x) => patchLogo(i, { name: x })} />
              <Text testid={`about-logo-link-${i}`} label="Link URL (optional — click destination)" value={logo.link} onChange={(x) => patchLogo(i, { link: x })} placeholder="https://..." />
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-[0.2em] text-muted block">Logo image (transparent PNG recommended)</label>
                <MediaUploader
                  images={logo.image_url ? [logo.image_url] : []}
                  videos={[]}
                  accept="image"
                  onChange={({ images }) => patchLogo(i, { image_url: images[0] || "" })}
                />
              </div>
            </div>
          ))}
        </div>
      </fieldset>

      {/* CLOSING */}
      <fieldset className="bg-[#141414] p-8 space-y-5 border border-[rgba(244,244,240,0.04)]">
        <legend className="text-xs uppercase tracking-[0.25em] text-gold px-3">Closing Promise</legend>
        <Text testid="about-closing-eyebrow" label="Eyebrow" value={draft.closing.eyebrow} onChange={(v) => setSection("closing", { eyebrow: v })} />
        <Textarea testid="about-closing-heading" rows={2} label="Heading" value={draft.closing.heading} onChange={(v) => setSection("closing", { heading: v })} />
        <div className="grid sm:grid-cols-2 gap-4">
          <Text testid="about-closing-cta-label" label="Button Label" value={draft.closing.cta_label} onChange={(v) => setSection("closing", { cta_label: v })} />
          <Text testid="about-closing-cta-link" label="Button Link" value={draft.closing.cta_link} onChange={(v) => setSection("closing", { cta_link: v })} placeholder="/shop" />
        </div>
      </fieldset>

      <div className="flex gap-3 sticky bottom-6 bg-[#0C0C0C] p-4 border border-[rgba(244,244,240,0.08)] z-10">
        <button type="submit" className="btn-gold flex-1" disabled={busy} data-testid="save-about-btn">
          {busy ? "Saving…" : "Save About Page"}
        </button>
        <button type="button" onClick={reset} className="btn-outline" data-testid="reset-about-btn">Reset</button>
      </div>
    </form>
  );
}

// --- tiny inline field helpers (kept local to avoid another one-off file) ---
function Text({ label, value, onChange, testid, placeholder }) {
  return (
    <div className="space-y-2">
      <label className="text-xs uppercase tracking-[0.2em] text-muted block">{label}</label>
      <input className="input-line" value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

function Textarea({ label, value, onChange, testid, rows = 4 }) {
  return (
    <div className="space-y-2">
      <label className="text-xs uppercase tracking-[0.2em] text-muted block">{label}</label>
      <textarea className="input-line resize-none" rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

function Toggle({ checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted cursor-pointer select-none">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#C8A97E]" data-testid={testid} />
      {checked ? "Enabled" : "Disabled"}
    </label>
  );
}
